/**
 * Platform console → Classrooms.
 *
 * CWY's own workspace has students and teachers exactly as a school does, so it
 * needs the same answer to "who teaches what": a teacher reaches a class — and
 * therefore its papers and its results — by holding a subject IN it. Until this
 * screen existed, a first-party teacher could be created under Staff and then
 * never given anything to teach, which made the Staff screen's own promise
 * ("assign their subjects afterwards on each classroom they take") unkeepable.
 *
 * Two differences from a school, both of them the workspace rather than the
 * screen: there are no sections, because CWY's students are one cohort per
 * board·grade rather than parallel classes; and the teachers offered are the
 * first-party TEACHER and COUNSELLOR memberships, which the platform staff list
 * already returns alongside the operators who hold no membership at all.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import type { Board, Grade, MembershipRole, PlatformRole } from '@cwy/config/contracts';
import { ClassroomsManager } from '@/components/admin/ClassroomsManager';

/** The shape PlatformStaffPage reads too — see GET /platform/staff. */
interface PlatformStaffRow {
  kind: 'PLATFORM' | 'TENANT';
  membershipId: string | null;
  name: string;
  role: PlatformRole | MembershipRole;
  enabled: boolean;
}

interface CohortsResponse {
  academicYear: { id: string; label: string };
  grades: { board: Board; grade: Grade }[];
}

export function PlatformClassroomsPage() {
  const staff = useQuery({
    queryKey: ['platform', 'staff'],
    queryFn: () => apiGet<PlatformStaffRow[]>('/platform/staff'),
  });
  const cohorts = useQuery({
    queryKey: ['platform', 'cohorts'],
    queryFn: () => apiGet<CohortsResponse>('/platform/cohorts'),
  });

  return (
    <ClassroomsManager
      title="Classrooms"
      subtitle="CWY’s own classes. Assign teachers their subjects here — that is what gives them their students’ work."
      endpoint="/platform/classrooms"
      queryKey={['platform', 'classrooms']}
      cohorts={cohorts.data?.grades ?? []}
      cohortsLoaded={!cohorts.isPending}
      allowSection={false}
      // A platform operator holds no membership, so only the TENANT rows —
      // CWY's own teaching staff — can be given a subject.
      teachers={(staff.data ?? [])
        .filter((row) => row.kind === 'TENANT' && row.enabled && row.membershipId)
        .map((row) => ({ membershipId: row.membershipId!, name: row.name }))}
    />
  );
}
